'use client';

import React from 'react';

import { MatchGameCard } from './match-game-card';

export function MatchGameBoardSkeleton() {
  return (
    <div className='grid grid-cols-3 items-center gap-3'>
      {[...Array(12)].map((_, idx) => (
        <div className='relative h-24 w-24 animate-pulse cursor-pointer' key={`skeleton-card-${idx}`}>
          <div className='h-2 w-2'>
            <div className='bg-blue backface-hidden absolute flex h-full w-full items-center justify-center rounded-lg opacity-10'></div>
          </div>
        </div>
      ))}
    </div>
  );
}

interface MatchGameBoardProps {
  setMoves: React.Dispatch<React.SetStateAction<number>>;
  finishGame: () => void;
  cardIds: Array<number>;
  cardSymbols: Array<string>;
}

export function MatchGameBoard({ setMoves, finishGame, cardIds, cardSymbols }: MatchGameBoardProps) {
  const [openCards, setOpenCards] = React.useState<Array<number>>([]);
  const [clearedCards, setClearedCards] = React.useState<Array<number>>([]);
  const [shouldDisableAllCards, setShouldDisableAllCards] = React.useState<boolean>(false);
  const timeout = React.useRef<ReturnType<typeof setTimeout>>(undefined);
  const totalCards = cardIds.length;

  const checkCompletion = React.useCallback(() => {
    if (clearedCards.length === totalCards) {
      finishGame();
    }
  }, [clearedCards.length, finishGame, totalCards]);

  const evaluate = React.useCallback(() => {
    const [first, second] = openCards;
    if (!first || !second) return null;
    setShouldDisableAllCards(false);
    // check if first card is equal second card
    if (first % (totalCards / 2) === second % (totalCards / 2)) {
      setClearedCards((prev) => [...prev, first, second]);
      setOpenCards([]);
      return;
    }
    // flip the cards back after 100ms duration
    timeout.current = setTimeout(() => {
      setOpenCards([]);
    }, 600);
  }, [openCards, totalCards]);

  const handleCardClick = React.useCallback(
    (id: number) => {
      if (openCards.length === 1) {
        // in this case we have alredy selected one card
        // this means that we are finishing a move
        setOpenCards((prev) => [...prev, id]);
        setMoves((moves) => moves + 1);
        setShouldDisableAllCards(true);
      } else {
        // in this case this is the first card we select
        if (timeout.current) {
          clearTimeout(timeout.current);
        }
        setOpenCards([id]);
      }
    },
    [openCards.length, setMoves],
  );

  const checkIsFlipped = React.useCallback(
    (id: number) => clearedCards.includes(id) || openCards.includes(id),
    [clearedCards, openCards],
  );

  const checkIsInactive = React.useCallback((id: number) => clearedCards.includes(id), [clearedCards]);

  React.useEffect(() => {
    let timeout = setTimeout(() => null);
    if (openCards.length === 2) {
      timeout = setTimeout(evaluate, 250);
    }
    return () => {
      clearTimeout(timeout);
    };
  }, [evaluate, openCards]);

  React.useEffect(() => {
    checkCompletion();
  }, [checkCompletion, clearedCards]);

  return (
    <div className='grid grid-cols-3 items-center gap-3'>
      {cardIds.map((id) => (
        <MatchGameCard
          id={id}
          isDisabled={shouldDisableAllCards}
          isFlipped={checkIsFlipped(id)}
          isInactive={checkIsInactive(id)}
          key={id}
          onClick={handleCardClick}
          symbol={cardSymbols[id % (totalCards / 2)] as string}
        />
      ))}
    </div>
  );
}
